import { computed, ref } from 'vue';
import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { TUserProfile } from '@/commons/types';
import type { ObjectId } from '@Types';
import { logout } from '@/commons/api';
import {
  resetActiveDateFormat,
  resetActiveShelterCountry,
  resetActiveShelterTimezone,
  setActiveDateFormat,
  setActiveShelterCountry,
  setActiveShelterTimezone,
} from '@Constants';
import { FEATURE_FLAGS, ROLES, SETTINGS_ACCESS_PERMISSIONS } from '@AuthModule';

// TODO :: Needs to be reviewed
const STORE_BASE_NAME = 'af-session-store';
const getKey = (key: string) => `${STORE_BASE_NAME}__${key}`;
const storeKeys = {
  environment: getKey('environment'),
  initialUserId: getKey('initial-user-id'),
  isInvalidSession: getKey('isInvalidSession'),
  isLogged: getKey('is-logged'),
  isSU: getKey('is-super-user'),
  isSUGLO: getKey('is-super-user-glo'),
  logoutMessage: getKey('logout-message'),
  permissions: getKey('permissions'),
  shelterProfile: getKey('shelter-profile'),
  pendingTwoFa: getKey('pending-2fa'),
  pendingTwoFaReadiness: getKey('pending-2fa-readiness'),
  pendingPasswordExpired: getKey('pending-password-expired'),
  useNewPlatform: getKey('useNewPlatform'),
  userProfile: getKey('user-profile'),
};

export default defineStore('session', () => {
  // State
  const isProfileReady = ref(false);
  // NOTE :: 'local' | 'demo' | 'staging' | 'production', straight from the api's config('app.env').
  // It arrives beside `shelter` on the profile response, not inside it. Stored so a page reload
  // does not lose it — see useEnvironment.
  const environment = useStorage<string | null>(storeKeys.environment, null);
  const initialUserId = useStorage<string | null>(storeKeys.initialUserId, null);
  const isInvalidSession = useStorage(storeKeys.isInvalidSession, false);
  const isLogged = useStorage(storeKeys.isLogged, false);
  const realIsSU = useStorage(storeKeys.isSU, false);
  const realIsSUGLO = useStorage(storeKeys.isSUGLO, false);
  const permissions = useStorage<ROLES[]>(storeKeys.permissions, []);
  const userProfile = useStorage<TUserProfile | null>(storeKeys.userProfile, null, undefined, {
    serializer: StorageSerializers.object,
  });
  const shelterProfile = useStorage<any | null>(storeKeys.shelterProfile, null, undefined, {
    serializer: StorageSerializers.object,
  });
  const pendingTwoFa = useStorage<{
    challenge_id: string;
    channel_mode: 'email' | 'sms' | 'both';
    channels: ('email' | 'sms')[];
    available_channels?: ('email' | 'sms')[];
    selected_channel?: 'email' | 'sms' | null;
    email?: string;
    phone?: string;
    trusted_device_available?: boolean;
    trusted_days?: number;
  } | null>(storeKeys.pendingTwoFa, null, undefined, {
    serializer: StorageSerializers.object,
  });
  const pendingTwoFaReadiness = useStorage<{
    channel_mode: 'email' | 'sms' | 'both';
    channels?: ('email' | 'sms')[];
    available_channels?: ('email' | 'sms')[];
    email: string;
    phone?: string;
  } | null>(storeKeys.pendingTwoFaReadiness, null, undefined, {
    serializer: StorageSerializers.object,
  });
  const pendingPasswordExpired = useStorage<{
    email: string;
  } | null>(storeKeys.pendingPasswordExpired, null, undefined, {
    serializer: StorageSerializers.object,
  });
  const logoutMessage = useStorage<string>(storeKeys.logoutMessage, '');

  // Getters
  const isShelterAdmin = computed(() => userProfile.value?.role === 'admin');
  const simulatedRole = computed(() => userProfile.value?.simulate_role ?? null);
  const isSimulatingRole = computed(() => !!simulatedRole.value);

  // NOTE :: Many views check isSU / isSUGLO instead of can(). A simulating super user must see only the
  // simulated role, so both read false until the simulation ends. The route guard reads the stored real values.
  const isSU = computed(() => realIsSU.value && !isSimulatingRole.value);
  const isSUGLO = computed(() => realIsSUGLO.value && !isSimulatingRole.value);
  const bypassesPermissions = computed(() => isSU.value || isSUGLO.value);

  const currentUserId = computed<string | null>(() => {
    const id = userProfile.value?._id as unknown;

    if (!id) return null;

    if (typeof id === 'string') return id;

    const isObjectId = id && typeof id === 'object' && '$oid' in id;
    if (isObjectId) return String((id as ObjectId).$oid);

    return null;
  });

  const shelterHasProducts = computed(
    () => !!shelterProfile.value?.enable_products && !!shelterProfile.value?.products_length
  );

  const checkForPermissionSettings = computed(() => SETTINGS_ACCESS_PERMISSIONS.some((permission) => can(permission)));

  function can(permissionName: ROLES) {
    if (bypassesPermissions.value) {
      return true;
    }

    if (permissions.value && permissions.value.length > 0) {
      return permissions.value.includes(permissionName);
    }

    return false;
  }

  const canEditSummaryItems = computed(
    () => bypassesPermissions.value || isShelterAdmin.value || can(ROLES.EDIT_SUMMARY_ITEMS)
  );

  const canRecodeAccountCodeLines = computed(() => bypassesPermissions.value || isShelterAdmin.value);

  function getActiveFeatures() {
    if (!shelterProfile.value) return [];

    const flags = Object.values(FEATURE_FLAGS);

    return flags.filter((flag) => shelterProfile.value[flag]);
  }

  // Actions
  // NOTE :: Many views check `userProfile.role === 'admin'` instead of can(). While simulating, `role` holds the
  // simulated role, so those checks follow it too. `main_role` keeps the real one for the return path.
  function toEffectiveUserProfile(user: TUserProfile): TUserProfile {
    if (!user.simulate_role) return user;

    return { ...user, main_role: user.role, role: user.simulate_role.name };
  }

  function shelterIdFromProfile(shelter: any): string | null {
    const id = shelter?._id;
    if (id == null) return null;
    return typeof id === 'object' && id !== null && '$oid' in id ? id.$oid : String(id);
  }

  async function resetCartStoresIfShelterContextChanged(prevProfile: any | null, newShelter: any): Promise<void> {
    const previousShelterId = shelterIdFromProfile(prevProfile);
    const newShelterId = shelterIdFromProfile(newShelter);

    const shelterIdentityChanged =
      previousShelterId != null && newShelterId != null && previousShelterId !== newShelterId;

    if (!shelterIdentityChanged) return;

    // NOTE :: Lazy-load the cart store to avoid an eager circular dependency at session-store init
    // (ShoppingCart store -> session store). Keep this dynamic import.
    const { default: useShoppingCartStore } = await import('@/modules/Entities/Shelter/modules/ShoppingCart/store');

    useShoppingCartStore().resetCart();
  }

  // TODO : Create interface for initProfile
  async function initProfile(profileData: any) {
    const prevShelterProfile = shelterProfile.value;

    initialUserId.value = userProfile.value?._id;
    realIsSUGLO.value =
      profileData.super_admin_glo ||
      profileData.user.role === 'af-admin-glo' ||
      profileData.user.role === 'af-admin-staff';
    realIsSU.value = realIsSUGLO.value || profileData.super_admin;
    userProfile.value = toEffectiveUserProfile(profileData.user);
    shelterProfile.value = profileData.shelter;
    environment.value = profileData.environment ?? null;
    permissions.value = profileData.permissions;

    // NOTE :: Push the shelter's date format into the constants module so pure helpers
    // (date.helper.ts) can read it without importing a store. Covers first login,
    // login-as, and the shelter-settings save path (which re-runs initProfile).
    setActiveDateFormat(profileData.shelter?.date_format);
    setActiveShelterTimezone(profileData.shelter?.timezone);
    setActiveShelterCountry(profileData.shelter?.country);

    isLogged.value = true;
    pendingTwoFa.value = null;
    pendingTwoFaReadiness.value = null;
    pendingPasswordExpired.value = null;

    // NOTE :: Admin and /shelter use different TheMainLayout instances; a watch there misses
    // shelter switches (new layout mounts with no prior watch oldValue). Reset here instead.
    await resetCartStoresIfShelterContextChanged(prevShelterProfile, profileData.shelter);

    // NOTE :: Keep this console.log(). Gerald's request.
    console.log('Shelter ID:', shelterProfile.value?._id?.$oid);

    isProfileReady.value = true;
  }

  function forgetSimulatedRole() {
    if (!userProfile.value) return;

    const role = userProfile.value.main_role ?? userProfile.value.role;

    userProfile.value = { ...userProfile.value, main_role: undefined, role, simulate_role: null };
  }

  async function clearProfile() {
    isProfileReady.value = false;
    realIsSU.value = false;
    realIsSUGLO.value = false;
    userProfile.value = null;
    shelterProfile.value = null;
    environment.value = null;
    permissions.value = [];
    pendingTwoFa.value = null;
    pendingTwoFaReadiness.value = null;
    pendingPasswordExpired.value = null;

    resetActiveDateFormat();
    resetActiveShelterTimezone();
    resetActiveShelterCountry();
  }

  function reset() {
    isProfileReady.value = false;
    isLogged.value = false;
    initialUserId.value = null;
    isInvalidSession.value = null;
    realIsSU.value = false;
    realIsSUGLO.value = false;
    permissions.value = null;
    shelterProfile.value = null;
    environment.value = null;
    userProfile.value = null;
    pendingTwoFa.value = null;
    pendingTwoFaReadiness.value = null;
    pendingPasswordExpired.value = null;

    resetActiveDateFormat();
    resetActiveShelterTimezone();
    resetActiveShelterCountry();

    localStorage.clear();
    sessionStorage.clear();
  }

  async function end() {
    try {
      await logout();
    } catch {
      return;
    } finally {
      isLogged.value = false;
      clearProfile();
    }
  }

  return {
    bypassesPermissions,
    canEditSummaryItems,
    canRecodeAccountCodeLines,
    checkForPermissionSettings,
    currentUserId,
    environment,
    initialUserId,
    isInvalidSession,
    isLogged,
    isProfileReady,
    isSU,
    isSUGLO,
    isShelterAdmin,
    isSimulatingRole,
    logoutMessage,
    permissions,
    shelterHasProducts,
    shelterProfile,
    simulatedRole,
    pendingTwoFa,
    pendingTwoFaReadiness,
    pendingPasswordExpired,
    userProfile,

    can,

    end,
    forgetSimulatedRole,
    initProfile,
    clearProfile,
    reset,

    getActiveFeatures,
  };
});
